<template>
  <div class="d-flex flex-column-fluid invoices-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Invoice Requests</h1>
            <p class="subtitle">Track and manage all invoice financing requests</p>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section mb-3">
        <div class="filters-bar">
          <div class="filters-controls">
            <div class="filter-group search-group">
              <i class="las la-search filter-group-icon"></i>
              <input
                type="text"
                class="filter-input search-input"
                v-model="searchTerm"
                @input="handleSearchInput"
                placeholder="Search by invoice reference, finance request..."
              />
              <i 
                v-if="searchTerm" 
                class="las la-times clear-input-icon" 
                @click="clearSearch"
                title="Clear search"
              ></i>
            </div>
            <div class="filter-group business-group">
              <div class="filter-dropdown-wrapper">
                <button 
                  class="filter-dropdown-toggle"
                  @click="toggleBusinessDropdown"
                  :title="selectedBusinesses.length > 0 ? selectedBusinesses.map(b => b.name).join(', ') : ''"
                >
                  <i class="las la-building filter-toggle-icon"></i>
                  <span v-if="selectedBusinesses.length === 0" class="placeholder-text">
                    All businesses
                  </span>
                  <span v-else class="selected-text">
                    <template v-if="selectedBusinesses.length === 1">
                      {{ selectedBusinesses[0].name }}
                    </template>
                    <template v-else-if="selectedBusinesses.length === 2">
                      {{ selectedBusinesses[0].name }}, {{ selectedBusinesses[1].name }}
                    </template>
                    <template v-else>
                      {{ selectedBusinesses[0].name }}, {{ selectedBusinesses[1].name }} +{{ selectedBusinesses.length - 2 }}
                    </template>
                  </span>
                  <i class="las la-angle-down dropdown-chevron"></i>
                </button>
                
                <div v-if="showBusinessDropdown" class="filter-dropdown-menu">
                  <div class="dropdown-search">
                    <i class="las la-search search-icon"></i>
                    <input
                      type="text"
                      class="search-input"
                      v-model="businessSearchTerm"
                      @input="searchBusinesses"
                      placeholder="Search businesses..."
                      @click.stop
                    />
                  </div>
                  
                  <div class="dropdown-options" @scroll="onBusinessDropdownScroll" ref="businessDropdownOptions">
                    <div v-if="loadingBusinesses && filteredBusinesses.length === 0" class="dropdown-loading">
                      <div class="spinner-border spinner-border-sm"></div>
                      <span>Loading...</span>
                    </div>
                    <div v-else-if="filteredBusinesses.length === 0 && !loadingBusinesses" class="dropdown-empty">
                      No businesses found
                    </div>
                    <template v-else>
                      <label 
                        v-for="business in filteredBusinesses" 
                        :key="business.id"
                        class="dropdown-option"
                      >
                        <input
                          type="checkbox"
                          :checked="selectedBusinesses.some(b => b.id === business.id)"
                          @change="toggleBusinessSelection(business)"
                        />
                        <span class="option-label">{{ business.name }}</span>
                      </label>
                      
                      <div v-if="loadingMoreBusinesses" class="dropdown-loading">
                        <div class="spinner-border spinner-border-sm"></div>
                        <span>Loading more...</span>
                      </div>
                    </template>
                  </div>
                  
                  <div v-if="selectedBusinesses.length > 0" class="dropdown-footer">
                    <button type="button" class="clear-btn" @click.stop="clearAllBusinessSelections">
                      Clear selection
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button 
            type="button"
            class="clear-all-filters-btn"
            @click="clearAllFilters"
            :disabled="!hasActiveFilters"
            title="Clear all filters"
          >
            <i class="las la-times"></i>
            Clear filters
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading invoice requests...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="isEmpty"
        >
          <i class="las la-file-invoice la-5x text-muted mb-3"></i>
          <div class="empty-title">No invoice requests found</div>
          <div class="empty-subtitle">
            Invoice requests will appear here once created
          </div>
        </div>

        <!-- Invoices Table -->
        <div v-else class="invoices-table-container">
          <financing-table
            :columns="tableColumns"
            :data="formattedInvoices"
            :grid-template="gridTemplate"
            :total-rows="totalInvoices"
            :current-page="currentPage"
            :page-size="pageSize"
            :table-name="'invoice-requests'"
            :empty-title="hasActiveFilters ? 'No invoices found for your filters' : 'No invoice requests found'"
            :empty-subtitle="hasActiveFilters ? 'Try adjusting your search or filters' : ''"
            :empty-icon="'las la-file-invoice'"
            @page-change="onPageChanged"
            @action="handleTableAction"
          />
        </div>
      </div>
    </div>
    
    <!-- PDF Viewer -->
    <pdf-viewer 
      v-if="showPDF" 
      :url="pdfUrl" 
      :name="pdfName"
      :showHeader="true"
      :forceExpansion="true" 
      :enableOverlayEffect="true" 
      @closePDF="closePDFViewer"
    />
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import PdfViewer from "@/components/pdfViewer.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "InvoicesPage",
  components: {
    FinancingTable,
    PdfViewer
  },
  data() {
    return {
      loading: true,
      loadingInvoices: false,
      error: null,
      
      // PDF Viewer
      showPDF: false,
      pdfUrl: null,
      pdfName: '',
      
      // Search
      searchTerm: "",
      searchDebounceTimer: null,
      
      // Business filter dropdown
      businessSearchTerm: "",
      selectedBusinesses: [],
      filteredBusinesses: [],
      loadingBusinesses: false,
      loadingMoreBusinesses: false,
      showBusinessDropdown: false,
      businessCurrentPage: 1,
      businessHasMore: true,
      businessDebounceTimer: null,
      
      // Table data
      invoices: [],
      currentPage: 1,
      pageSize: 25,
      totalInvoices: 0,
      
      // Grid template for FinancingTable
      gridTemplate: "135px 125px 135px 145px 135px 85px 135px 75px 135px 80px",
      
      // Table columns
      tableColumns: [
        {
          header: 'Finance Request',
          field: 'invoice_reference_number',
          type: 'text',
          width: '135px'
        },
        {
          header: 'Invoice Reference',
          field: 'invoice_reference',
          type: 'text',
          width: '125px'
        },
        {
          header: 'Invoice Amount',
          field: 'invoice_amount',
          type: 'currency',
          currencyField: 'currency',
          width: '135px',
          align: 'right'
        },
        {
          header: 'Requested Amount',
          field: 'initial_requested_amount',
          type: 'currency',
          currencyField: 'currency',
          width: '145px',
          align: 'right'
        },
        {
          header: 'Approval Amount',
          field: 'approval_amount',
          type: 'currency',
          currencyField: 'currency',
          width: '135px',
          align: 'right'
        },
        {
          header: 'Advance Rate',
          field: 'advance_rate',
          type: 'text',
          width: '85px',
          align: 'right'
        },
        {
          header: 'Purchase Amount',
          field: 'purchase_amount',
          type: 'currency',
          currencyField: 'currency',
          width: '135px',
          align: 'right'
        },
        {
          header: 'Fees Rate',
          field: 'transaction_fees_rate',
          type: 'text',
          width: '75px',
          align: 'right',
          class: 'text-right'
        },
        {
          header: 'Transaction Fees',
          field: 'transaction_fees',
          type: 'currency',
          currencyField: 'currency',
          width: '135px',
          align: 'right'
        },
        {
          header: 'File',
          field: 'file',
          type: 'action',
          action: 'download',
          icon: 'las la-file-download',
          tooltip: 'Download Invoice',
          width: '80px',
          align: 'center'
        }
      ]
    };
  },
  computed: {
    isEmpty() {
      return this.invoices.length === 0 && !this.loading && !this.error;
    },
    hasActiveFilters() {
      return this.selectedBusinesses.length > 0 || (this.searchTerm && this.searchTerm.trim());
    },
    formattedInvoices() {
      // Format the invoices to add percentage symbols and handle null values
      return this.invoices.map(invoice => ({
        ...invoice,
        advance_rate: invoice.advance_rate !== null && invoice.advance_rate !== undefined 
          ? `${invoice.advance_rate}%` 
          : '-',
        transaction_fees_rate: invoice.transaction_fees_rate !== null && invoice.transaction_fees_rate !== undefined 
          ? `${invoice.transaction_fees_rate}%` 
          : '-'
      }));
    }
  },
  mounted() {
    this.initializeData();
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.businessDebounceTimer) {
      clearTimeout(this.businessDebounceTimer);
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    
    // Clean up PDF viewer
    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = null;
    }
    this.showPDF = false;
  },
  methods: {
    async initializeData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Load invoices
        await this.fetchInvoices();
      } catch (error) {
        console.error("Error initializing data:", error);
        this.error = formatErrorMessage(error) || "Failed to load data. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    
    async fetchAllBusinesses(page = 1, append = false) {
      if (page === 1) {
        this.loadingBusinesses = true;
      } else {
        this.loadingMoreBusinesses = true;
      }
      
      try {
        const params = {
          page,
          page_size: 20,
          financing_enabled: true,
          list_view: 'True'
        };
        
        // Add search parameter if search term exists
        if (this.businessSearchTerm && this.businessSearchTerm.trim()) {
          params.search = this.businessSearchTerm.trim();
        }
        
        const response = await CompaniesService.query(params);
        
        const newBusinesses = (response.data || []).map(business => ({
          id: business.id,
          name: business.name
        }));
        
        if (append) {
          // Append to existing list for infinite scroll
          this.filteredBusinesses = [...this.filteredBusinesses, ...newBusinesses];
        } else {
          // Replace list for new search or initial load
          this.filteredBusinesses = newBusinesses;
        }
        
        // Update pagination state
        this.businessCurrentPage = page;
        this.businessHasMore = response.next !== null;
        
      } catch (error) {
        console.error("Error fetching businesses:", error);
      } finally {
        this.loadingBusinesses = false;
        this.loadingMoreBusinesses = false;
      }
    },
    
    async fetchInvoices(page = 1) {
      this.loadingInvoices = true;
      this.error = null;
      
      try {
        const params = {
          page,
          page_size: this.pageSize
        };
        
        // Add search parameter
        if (this.searchTerm && this.searchTerm.trim()) {
          params.search = this.searchTerm.trim();
        }
        
        // Add filters - send as array so Django can use .getlist()
        if (this.selectedBusinesses.length > 0) {
          params.business_id = this.selectedBusinesses.map(b => b.id);
        }
        
        const response = await FinancingService.getInvoiceRequests(params);
        
        this.invoices = response.data || [];
        this.totalInvoices = response.count || 0;
        this.currentPage = page;
        
      } catch (error) {
        console.error("Error fetching invoices:", error);
        this.error = formatErrorMessage(error) || "Failed to load invoice requests. Please try again.";
        this.invoices = [];
        this.totalInvoices = 0;
      } finally {
        this.loadingInvoices = false;
      }
    },
    
    // Search methods
    handleSearchInput() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      // Debounce the search API call
      this.searchDebounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchInvoices(1);
      }, 300);
    },
    
    clearSearch() {
      this.searchTerm = "";
      this.currentPage = 1;
      this.fetchInvoices(1);
    },
    
    // Business dropdown methods
    toggleBusinessDropdown() {
      this.showBusinessDropdown = !this.showBusinessDropdown;
      if (this.showBusinessDropdown) {
        // Reset and fetch businesses when opening dropdown
        this.businessCurrentPage = 1;
        this.businessHasMore = true;
        this.fetchAllBusinesses(1, false);
      }
    },
    
    searchBusinesses() {
      // Clear existing timer
      if (this.businessDebounceTimer) {
        clearTimeout(this.businessDebounceTimer);
      }
      
      // Debounce the search API call
      this.businessDebounceTimer = setTimeout(() => {
        this.businessCurrentPage = 1;
        this.businessHasMore = true;
        this.fetchAllBusinesses(1, false);
      }, 300);
    },
    
    onBusinessDropdownScroll(event) {
      const element = event.target;
      const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      
      // Load more when scrolled near bottom (within 50px)
      if (scrollBottom < 50 && !this.loadingMoreBusinesses && this.businessHasMore) {
        this.loadMoreBusinesses();
      }
    },
    
    async loadMoreBusinesses() {
      const nextPage = this.businessCurrentPage + 1;
      await this.fetchAllBusinesses(nextPage, true);
    },
    
    toggleBusinessSelection(business) {
      const index = this.selectedBusinesses.findIndex(b => b.id === business.id);
      if (index > -1) {
        this.selectedBusinesses.splice(index, 1);
      } else {
        this.selectedBusinesses.push(business);
      }
      this.applyFilters();
    },
    
    removeBusinessSelection(business) {
      const index = this.selectedBusinesses.findIndex(b => b.id === business.id);
      if (index > -1) {
        this.selectedBusinesses.splice(index, 1);
        this.applyFilters();
      }
    },
    
    clearAllBusinessSelections() {
      this.selectedBusinesses = [];
      this.businessSearchTerm = "";
      this.businessCurrentPage = 1;
      this.businessHasMore = true;
      this.fetchAllBusinesses(1, false);
      this.applyFilters();
    },
    
    applyFilters() {
      this.currentPage = 1;
      this.fetchInvoices(1);
    },
    
    clearAllFilters() {
      this.selectedBusinesses = [];
      this.searchTerm = "";
      this.businessSearchTerm = "";
      this.businessCurrentPage = 1;
      this.businessHasMore = true;
      this.fetchAllBusinesses(1, false);
      this.applyFilters();
    },
    
    handleClickOutside(event) {
      const target = event.target;
      if (!target.closest('.filter-dropdown-wrapper')) {
        this.showBusinessDropdown = false;
      }
    },
    
    onPageChanged(page) {
      this.fetchInvoices(page);
    },
    
    async handleTableAction(payload) {
      const { action, row } = payload;
      
      if (action === 'download') {
        if (!row.file) {
          console.warn('No file URL found for row:', row);
          this.$showAlert('No file available for this invoice', 'warning');
          return;
        }
                
        try {
          // Fetch the PDF file as a blob
          const response = await fetch(row.file);
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF file: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          
          // Create a blob URL for the PDF viewer
          this.pdfUrl = URL.createObjectURL(blob);
          this.pdfName = `Invoice-${row.invoice_reference || 'document'}.pdf`;
                    
          // Show the PDF viewer
          this.showPDF = true;
        } catch (error) {
          console.error('Error loading PDF:', error);
          this.$showAlert('Failed to load PDF file. Opening in new tab instead.', 'warning');
          // Fallback to opening in new tab
          window.open(row.file, '_blank');
        }
      }
    },
    
    closePDFViewer() {
      this.showPDF = false;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.pdfName = '';
    },
    
    formatMoney(amount, currency) {
      if (amount === null || amount === undefined) return '-';
      
      const numAmount = typeof amount === 'object' && amount.amount 
        ? parseFloat(amount.amount) 
        : parseFloat(amount);
      
      if (isNaN(numAmount)) return '-';
      
      return `${numAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} ${currency || ''}`;
    }
  }
};
</script>

<style scoped lang="scss">
.invoices-page {
  .page-header {
    background: #fff;
    padding: 18px 24px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .header-title {
        .title {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
        
        .subtitle {
          font-size: 13px;
          color: #6c757d;
          margin: 2px 0 0;
        }
      }
    }
  }
  
  .filters-section {
    background: #fff;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
  }

  .filters-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .filters-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  .filter-group {
    position: relative;
    flex-shrink: 0;
  }

  .search-group {
    flex: 1;
    min-width: 200px;
    max-width: 360px;
  }

  .business-group {
    min-width: 200px;
    max-width: 280px;
  }

  @media (max-width: 768px) {
    .filters-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .filters-controls {
      flex-direction: column;
    }

    .search-group {
      max-width: none;
    }

    .business-group {
      max-width: none;
    }

    .clear-all-filters-btn {
      align-self: flex-end;
    }
  }

  .filter-group-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 15px;
    pointer-events: none;
  }

  .filter-input {
    width: 100%;
    height: 38px;
    padding: 0 36px 0 36px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    background: #fff;
  }

  .filter-input::placeholder {
    color: #9ca3af;
  }

  .filter-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
  }

  .clear-input-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 16px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: color 0.15s, background 0.15s;
  }

  .clear-input-icon:hover {
    color: #dc2626;
    background: #fef2f2;
  }

  .clear-all-filters-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;
  }

  .clear-all-filters-btn:hover:not(:disabled) {
    color: #374151;
    background: #f3f4f6;
  }

  .clear-all-filters-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .clear-all-filters-btn i {
    font-size: 16px;
  }

  .filter-dropdown-wrapper {
    position: relative;
  }

  .filter-dropdown-wrapper .filter-dropdown-toggle {
    width: 100%;
    height: 38px;
    padding: 0 12px 0 36px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    min-width: 0;
  }

  .filter-dropdown-toggle:hover {
    border-color: #d1d5db;
  }

  .filter-toggle-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 15px;
    pointer-events: none;
  }

  .filter-dropdown-toggle .placeholder-text {
    color: #9ca3af;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  .filter-dropdown-toggle .selected-text {
    color: #2563eb;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  .dropdown-chevron {
    font-size: 14px;
    color: #6b7280;
    flex-shrink: 0;
    margin-left: 8px;
  }

  .filter-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 1000;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    max-height: 320px;
    display: flex;
    flex-direction: column;

    .dropdown-search {
      position: relative;
      padding: 8px;
      border-bottom: 1px solid #e5e7eb;

      .search-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #9ca3af;
        font-size: 14px;
        pointer-events: none;
      }

      .search-input {
        width: 100%;
        height: 32px;
        padding: 4px 12px 4px 32px;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        font-size: 13px;
        outline: none;

        &:focus {
          border-color: #2563eb;
        }
      }
    }

    .dropdown-options {
      flex: 1;
      overflow-y: auto;
      max-height: 240px;

      .dropdown-loading {
        padding: 16px;
        text-align: center;
        color: #6b7280;
        font-size: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      .dropdown-empty {
        padding: 24px;
        text-align: center;
        color: #6b7280;
        font-size: 13px;
      }

      .dropdown-option {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        cursor: pointer;
        transition: background-color 0.15s;
        margin: 0;

        &:hover {
          background-color: #f9fafb;
        }

        input[type="checkbox"] {
          width: 16px;
          height: 16px;
          margin-right: 10px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .option-label {
          font-size: 13px;
          color: #374151;
          flex: 1;
        }
      }
    }

    .dropdown-footer {
      padding: 8px;
      border-top: 1px solid #e5e7eb;

      .clear-btn {
        width: 100%;
        height: 32px;
        padding: 4px 12px;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        color: #ef4444;
        cursor: pointer;
        transition: all 0.15s;

        &:hover {
          background: #fef2f2;
          border-color: #ef4444;
        }
      }
    }
  }

  .loading-state,
  .error-state,
  .empty-state {
    background: #fff;
    padding: 60px 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    text-align: center;
    
    .spinner-border {
      width: 3rem;
      height: 3rem;
      border-width: 0.25em;
      color: #3699ff;
    }
    
    .loading-text {
      margin-top: 16px;
      font-size: 14px;
      color: #6c757d;
    }
    
    .empty-title {
      font-size: 20px;
      font-weight: 600;
      color: #1a1a1a;
      margin-top: 16px;
    }
    
    .empty-subtitle {
      font-size: 14px;
      color: #6c757d;
      margin-top: 8px;
    }
    
    i.las {
      color: #b5b5c3;
    }
  }
  
  .invoices-table-container {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 20px;
  }
}
</style>
